<template>
  <div class="invoicing-page">

    <!-- Page title bar — heading intentionally removed; the bar itself
         stays so the changed-after-approve alert below still has a place
         to render when there's something to show. -->
    <div class="page-title-bar">
      <b-alert
        v-if="alerts.changed_after_approve_count > 0"
        show
        variant="warning"
        class="changed-after-approve-banner"
      >
        <b-icon icon="exclamation-triangle-fill" class="mr-1" />
        {{ alerts.changed_after_approve_count }} Invoice{{ alerts.changed_after_approve_count !== 1 ? 's' : '' }}
        have been changed since approval.
        <a href @click.prevent="showChangedAfterApprove">Click here</a>
        to filter to &ldquo;Changed After Approval&rdquo; and reapprove.
      </b-alert>
    </div>

    <!-- Top-level tabs (matching Company Profile style) -->
    <div class="site-tabs">
      <div
        v-for="tab in tabs"
        :key="tab.key"
        class="site-tab"
        :class="{ 'site-tab--active': activeTab === tab.key }"
        @click="activeTab = tab.key"
      >
        {{ tab.label }}
      </div>
    </div>

    <!-- Tab content -->
    <div class="tab-content-area">
      <b-container fluid class="px-4 py-4">

        <!-- Billing Status -->
        <BillingStatusTab v-if="activeTab === 'billing-status'" />

        <!-- Generate Invoices -->
        <GenerateInvoicesTab v-else-if="activeTab === 'generate-invoices'" :filter-mode="filterMode" />

        <!-- Invoicing -->
        <InvoicingTab v-else-if="activeTab === 'invoicing'" />

      </b-container>
    </div>

  </div>
</template>

<script>
import { defineComponent, ref, watch } from '@vue/composition-api'
import { useRoute, useRouter } from 'vue2-helpers/vue-router'
import BillingStatusTab from '@components/billing/BillingStatusTab.vue'
import GenerateInvoicesTab from '@components/billing/GenerateInvoicesTab.vue'
import InvoicingTab from '@components/billing/InvoicingTab.vue'
import { useInvoiceAlerts } from '@composables'

export default defineComponent({
  name: 'Invoicing',
  title: 'Invoicing',
  components: {
    BillingStatusTab,
    GenerateInvoicesTab,
    InvoicingTab
  },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const { alerts, filterRequest } = useInvoiceAlerts()

    // ?tab=generate-invoices&filter=needs-attention|changed-after-approve —
    // set by the header warning icon / this page's own banner below. Only
    // consulted for the initial load (direct link / refresh) — see the
    // filterRequest watcher below for while-mounted updates.
    const activeTab = ref(route.query.tab || 'billing-status')
    const filterMode = ref(route.query.filter || null)

    // Vue Router reuses this component instance for query-only navigations
    // on the same route (e.g. clicking the header warning icon again while
    // already on this page) — the refs above are only seeded once at
    // setup(), so without this watcher a second click wouldn't do anything.
    // This alone isn't reliable though: navigating to a query that already
    // matches the current route is a silent no-op in Vue Router (no
    // navigation, route.query never changes), so a second click on the
    // icon while the URL already has ?filter=needs-attention wouldn't fire
    // this watcher at all. filterRequest below is what actually guarantees
    // every click works.
    watch(
      () => [route.query.tab, route.query.filter],
      ([newTab, newFilter]) => {
        if (newTab) activeTab.value = newTab
        filterMode.value = newFilter || null
      }
    )

    // AppNavbar.vue's warning icon calls requestFilter() on every click,
    // tagged with a strictly-increasing nonce — so unlike route.query,
    // this always fires the watcher below even for a repeat click with the
    // same filter value.
    watch(filterRequest, (req) => {
      if (!req) return
      activeTab.value = 'generate-invoices'
      filterMode.value = req.filter
    })

    const tabs = [
      { key: 'billing-status',    label: 'Billing Status' },
      { key: 'generate-invoices', label: 'Generate Invoices' },
      { key: 'invoicing',         label: 'Invoicing' }
    ]

    const showChangedAfterApprove = () => {
      activeTab.value = 'generate-invoices'
      filterMode.value = 'changed-after-approve'
      router.replace({
        query: { ...route.query, tab: 'generate-invoices', filter: 'changed-after-approve' },
      }).catch(() => {})
    }

    return {
      activeTab,
      filterMode,
      tabs,
      alerts,
      showChangedAfterApprove,
    }
  }
})
</script>

<style scoped>
.invoicing-page {
  min-height: 100vh;
  background: #f4f6f8;
}

/* Page title bar */
.page-title-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 24px 16px;
  background: #fff;
  flex-wrap: wrap;
}

.changed-after-approve-banner {
  margin: 0 0 0 auto;
  padding: 10px 16px;
  font-size: 12px;
}

/* Tab bar — matches Company Profile exactly */
.site-tabs {
  display: flex;
  background: #b8cdd1;  /* light teal-grey inactive background */
  border-bottom: none;
}

.site-tab {
  flex: 1;
  text-align: center;
  padding: 14px 16px;
  font-size: 13px;
  font-weight: 500;
  color: #32325d;
  cursor: pointer;
  user-select: none;
  transition: background 0.15s ease, color 0.15s ease;
  white-space: nowrap;
}

.site-tab:hover {
  background: #a5bec3;
  color: #1c4a50;
}

.site-tab--active {
  background: #1c4a50;
  color: #ffffff;
  font-weight: 600;
}

/* Content area */
.tab-content-area {
  background: #f4f6f8;
  min-height: calc(100vh - 120px);
}
</style>
